import { supabase, isSupabaseConfigured } from "./supabase";

const rowToLead = (row) => ({
  id:                row.id,
  createdAt:         row.created_at,
  source:            row.source,
  name:              row.name,
  email:             row.email,
  company:           row.company,
  phone:             row.phone,
  message:           row.message,
  payload:           row.payload,
  status:            row.status,
  notes:             row.notes,
  convertedClientId: row.converted_client_id,
  // Workflow Pro fields (added 2026-06-02, migration leads_workflow_stage).
  // Drive the outbound funnel — prospected → outreached → strategized → won/lost.
  workflowStage:     row.workflow_stage,
  emailStatus:       row.email_status,      // valid | no_mx | nxdomain | duplicate
  emailCheckedAt:    row.email_checked_at,
  prospectorJson:    row.prospector_json,
  outreachEmailJson: row.outreach_email_json,
  strategyJson:      row.strategy_json,
  prospectedAt:      row.prospected_at,
  outreachedAt:      row.outreached_at,
  strategizedAt:     row.strategized_at,
  scheduledAt:       row.scheduled_at,
  wonAt:             row.won_at,
  lostAt:            row.lost_at,
});

// "ai_prospector" lets the inbound Leads view filter out AI-prospected rows
// (or surface them separately) — they're outbound, not real inbound interest.
const VALID_SOURCES  = ["contact", "onboarding", "chat", "ai_prospector", "other"];
const VALID_STATUSES = ["new", "contacted", "qualified", "converted", "lost"];
const VALID_WORKFLOW_STAGES = ["prospected", "outreached", "strategized", "scheduled", "won", "lost"];

// Public forms call this on submit. Goes through the /api/lead serverless
// function (server-side service_role insert) rather than direct from the
// browser — that way the public anon key can't be used to spam the table.
// Fire-and-forget: errors are swallowed + warned so a Formspree success
// isn't undone by a transient backend hiccup.
export const captureLead = async ({ source, name, email, company, phone, message, payload }) => {
  try {
    const res = await fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: VALID_SOURCES.includes(source) ? source : "other",
        name, email, company, phone, message, payload,
      }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      console.warn(`captureLead failed (${res.status}):`, text.slice(0, 200));
      return false;
    }
    return true;
  } catch (err) {
    console.warn("captureLead threw:", err?.message);
    return false;
  }
};

// Owner-side manual lead creation — for leads that come from phone calls,
// networking events, referrals, etc. that didn't go through a public form.
// RLS allows the owner full insert access; no need to go through /api/lead
// (which is rate-limited for anonymous traffic).
export const createLeadAsOwner = async ({ source, name, email, company, phone, message, status, notes }) => {
  if (!isSupabaseConfigured()) throw new Error("Supabase is not configured.");
  const { data, error } = await supabase
    .from("leads")
    .insert({
      source:  VALID_SOURCES.includes(source)   ? source : "other",
      status:  VALID_STATUSES.includes(status)  ? status : "new",
      name:    name    || null,
      email:   email   || null,
      company: company || null,
      phone:   phone   || null,
      message: message || null,
      notes:   notes   || null,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return rowToLead(data);
};

// Owner-side: list every lead, newest first. RLS keeps this to owner only.
export const listLeads = async () => {
  if (!isSupabaseConfigured()) return [];
  const { data, error } = await supabase
    .from("leads")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data || []).map(rowToLead);
};

// Partial update — same shape as updateDeliverable. Used to change status
// and notes from the AgencyOS Leads tab.
export const updateLead = async (id, patch) => {
  if (!isSupabaseConfigured()) throw new Error("Supabase is not configured.");
  const payload = {};
  if ("status" in patch) {
    if (!VALID_STATUSES.includes(patch.status)) {
      throw new Error(`Invalid status: ${patch.status}`);
    }
    payload.status = patch.status;
  }
  if ("notes" in patch) payload.notes = patch.notes;
  if ("convertedClientId" in patch) payload.converted_client_id = patch.convertedClientId || null;
  const { data, error } = await supabase
    .from("leads")
    .update(payload)
    .eq("id", id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return rowToLead(data);
};

export const deleteLead = async (id) => {
  if (!isSupabaseConfigured()) return;
  const { error } = await supabase.from("leads").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// Workflow Pro — bulk-insert leads from the Prospector AI agent output.
// Each item from the agent's JSON array becomes one lead row in the
// "prospected" workflow stage. The full agent item is kept on
// prospector_json so the Outreach agent can ground its email on the
// original prospect signal even after the human edits surface fields.
export const createProspectedLeads = async (items) => {
  if (!isSupabaseConfigured()) throw new Error("Supabase is not configured.");
  if (!Array.isArray(items) || items.length === 0) return [];
  const now = new Date().toISOString();
  const rows = items.map((item) => ({
    source: "ai_prospector",
    status: "new",                        // legacy NOT NULL column
    workflow_stage: "prospected",
    prospector_json: item,
    prospected_at: now,
    name:    item.name    || item.business || null,
    email:   item.email   || null,
    company: item.company || item.name     || null,
    phone:   item.phone   || null,
    message: item.pain    || item.why      || null,
    notes:   item.budget  ? `Budget: ${item.budget}` : null,
    // Grounded prospector pre-validates deliverability server-side; persist it
    // so the lead is gated correctly from the moment it lands (no wait for cron).
    email_status:     item.email_status || null,
    email_checked_at: item.email_status ? now : null,
  }));
  const { data, error } = await supabase.from("leads").insert(rows).select();
  if (error) throw new Error(error.message);
  return (data || []).map(rowToLead);
};

// Update a lead's workflow stage + optionally write the agent JSON output
// and bump the matching per-stage timestamp.
// Used by callAgent in AgencyOSContext after the Outreach / Strategist
// agents finish, and by the Kanban view when the owner drags a card.
export const setLeadWorkflowStage = async (id, stage, patch = {}) => {
  if (!isSupabaseConfigured()) throw new Error("Supabase is not configured.");
  if (!VALID_WORKFLOW_STAGES.includes(stage)) {
    throw new Error(`Invalid workflow_stage: ${stage}`);
  }
  const payload = { workflow_stage: stage };
  const now = new Date().toISOString();
  // Stamp the appropriate timestamp column so we can chart cycle time later.
  const stampCol = `${stage}_at`;
  payload[stampCol] = now;
  // Optional agent output snapshots.
  if ("outreachEmailJson" in patch) payload.outreach_email_json = patch.outreachEmailJson;
  if ("strategyJson" in patch)      payload.strategy_json       = patch.strategyJson;
  if ("notes" in patch)             payload.notes               = patch.notes;
  const { data, error } = await supabase
    .from("leads").update(payload).eq("id", id).select().single();
  if (error) throw new Error(error.message);
  return rowToLead(data);
};
